import type { TelegramUser } from '@guildrise/shared';
import { retrieveRawInitData } from '@telegram-apps/sdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, authenticate, setAccessToken, subscribeAuthFailure } from '../shared/api.js';

/**
 * Четыре состояния экрана, берущего данные с сервера. Не три.
 * Пропущенное «пусто» — самая частая ошибка.
 */
export type ScreenState =
  | { status: 'loading' }
  | { status: 'empty' }
  | { status: 'error'; code: string; message: string }
  | { status: 'locked'; message: string; busy: boolean }
  | { status: 'ready'; user: TelegramUser; accessToken: string };

/**
 * Вход в игру: берём initData у Telegram и обмениваем на токен.
 *
 * Повтор только по кнопке игрока: POST автоматически не повторяется —
 * запрос мог дойти и выполниться, а потеряться мог ответ.
 */
export function useAuth(): {
  state: ScreenState;
  retry: () => void;
  unlock: (key: string) => void;
} {
  const [state, setState] = useState<ScreenState>({ status: 'loading' });

  // Обесценивает результат предыдущей попытки, чтобы медленный ответ
  // не перезаписал состояние, полученное после повтора.
  const runIdRef = useRef(0);

  const run = useCallback(async (accessKey?: string): Promise<void> => {
    runIdRef.current += 1;
    const runId = runIdRef.current;
    const isCurrent = (): boolean => runIdRef.current === runId;
    setAccessToken(null);

    setState(
      accessKey === undefined
        ? { status: 'loading' }
        : { status: 'locked', message: '', busy: true },
    );

    let initData: string;
    try {
      // Берём СЫРУЮ строку initData, а не разобранный объект: Telegram
      // подписывает именно её, и любое преобразование ломает HMAC.
      initData = retrieveRawInitData() ?? '';
    } catch {
      // Страница открыта вне Telegram — это не ошибка сервера.
      if (isCurrent()) setState({ status: 'empty' });
      return;
    }

    if (initData.length === 0) {
      if (isCurrent()) setState({ status: 'empty' });
      return;
    }

    try {
      const result = await authenticate(initData, accessKey);
      if (isCurrent()) {
        setAccessToken(result.accessToken);
        setState({ status: 'ready', user: result.user, accessToken: result.accessToken });
      }
    } catch (error) {
      if (!isCurrent()) return;
      const isApiError = error instanceof ApiError;
      if (
        isApiError &&
        (error.code === 'CLOSED_ACCESS_REQUIRED' ||
          (accessKey !== undefined && error.code === 'RATE_LIMITED'))
      ) {
        setState({
          status: 'locked',
          message:
            error.code === 'RATE_LIMITED'
              ? 'Слишком много попыток. Подожди минуту.'
              : accessKey === undefined
                ? ''
                : 'Ключ не подошёл',
          busy: false,
        });
        return;
      }
      setState({
        status: 'error',
        code: isApiError ? error.code : 'INTERNAL',
        message: isApiError ? error.message : 'Сервер недоступен',
      });
    }
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeAuthFailure((error) => {
      runIdRef.current += 1;
      setState(
        error.code === 'CLOSED_ACCESS_REQUIRED'
          ? { status: 'locked', message: error.message, busy: false }
          : { status: 'error', code: error.code, message: error.message },
      );
    });
    void run();
    return () => {
      unsubscribe();
      // Размонтирование тоже обесценивает текущую попытку.
      runIdRef.current += 1;
      setAccessToken(null);
    };
  }, [run]);

  const retry = useCallback(() => {
    void run();
  }, [run]);

  const unlock = useCallback(
    (key: string) => {
      void run(key);
    },
    [run],
  );
  return { state, retry, unlock };
}
